import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { sponsorEndpoints } from '../../api';
import { Badge, Button, Panel } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT } from './shared';

/**
 * Who sponsors this event (D-87).
 *
 * Attaching rather than uploading: the sponsor already exists with its logos,
 * so this says which of them are on this show, at what level, and in what
 * order — and the event page, the content editor's sponsor blocks and the
 * ticket designs all read the same list.
 */

export function SponsorsPanel({ detail, onError, can }: PanelProps) {
  const queryClient = useQueryClient();
  const eventId = detail.event.id;
  const manage = can('sponsors.manage');
  const [sponsorId, setSponsorId] = useState('');
  const [tier, setTier] = useState('');
  const [label, setLabel] = useState('');

  const attached = useQuery({ queryKey: ['event-sponsors', eventId], queryFn: () => sponsorEndpoints.forEvent(eventId) });
  const all = useQuery({ queryKey: ['sponsors', false], queryFn: () => sponsorEndpoints.list(false) });
  const tiers = useQuery({ queryKey: ['sponsor-tiers'], queryFn: sponsorEndpoints.tiers });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['event-sponsors', eventId] });
  const fail = (cause: ApiError) => onError(cause);

  const attach = useMutation({
    mutationFn: () => sponsorEndpoints.attach(eventId, { sponsor_id: sponsorId, tier_code: tier || null, recognition_label: label || null, sort_order: attached.data?.sponsors.length ?? 0 }),
    onSuccess: () => {
      setSponsorId('');
      setTier('');
      setLabel('');
      refresh();
    },
    onError: fail,
  });
  const detach = useMutation({ mutationFn: (id: string) => sponsorEndpoints.detach(eventId, id), onSuccess: refresh, onError: fail });

  if (!can('sponsors.view')) return <Panel title="Sponsors"><p className="px-5 py-4 text-sm text-ink-600">You do not have permission to see sponsors.</p></Panel>;

  const available = (all.data?.sponsors ?? []).filter((s) => !(attached.data?.sponsors ?? []).some((a) => a.sponsor_id === s.id));

  return (
    <div className="space-y-4">
      <Panel title="Sponsors on this event" description="Sponsors are kept once and attached here, so a new logo in March changes every page and every ticket at once.">
        {attached.data?.sponsors.length === 0 ? (
          <p className="px-5 py-4 text-sm text-ink-600">No sponsors on this event yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {(attached.data?.sponsors ?? []).map((row) => (
              <li key={row.sponsor_id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                {row.logo_file_id ? <img src={`/api/public/media/${row.logo_file_id}`} alt="" className="h-8 w-20 rounded bg-page object-contain" /> : null}
                <span className="text-sm font-medium text-ink-900">{row.display_name ?? row.name}</span>
                {row.tier_code ? <Badge tone="accent">{row.tier_code.toLowerCase()}</Badge> : null}
                {row.recognition_label ? <span className="text-sm text-ink-600">{row.recognition_label}</span> : null}
                {manage ? (
                  <Button variant="danger" className="ml-auto" disabled={detach.isPending} onClick={() => detach.mutate(row.sponsor_id)}>
                    Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {manage ? (
        <Panel title="Attach a sponsor">
          <div className="flex flex-wrap items-end gap-3 px-5 py-4">
            <label className="text-sm">
              <span className="text-xs font-semibold text-ink-400 uppercase">Sponsor</span>
              <select value={sponsorId} onChange={(e) => setSponsorId(e.target.value)} className={`${INPUT} mt-1 block`}>
                <option value="">Choose…</option>
                {available.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.display_name ?? row.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs font-semibold text-ink-400 uppercase">Level</span>
              <select value={tier} onChange={(e) => setTier(e.target.value)} className={`${INPUT} mt-1 block`}>
                <option value="">Their usual level</option>
                {(tiers.data?.tiers ?? []).map((row) => (
                  <option key={row.code} value={row.code}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs font-semibold text-ink-400 uppercase">Called</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Opening night sponsor" className={`${INPUT} mt-1 block w-64`} />
            </label>
            <Button variant="primary" disabled={!sponsorId || attach.isPending} onClick={() => attach.mutate()}>
              Attach
            </Button>
            {available.length === 0 && (all.data?.sponsors.length ?? 0) > 0 ? <p className="text-sm text-ink-600">Every sponsor is already attached.</p> : null}
            {(all.data?.sponsors.length ?? 0) === 0 ? (
              <p className="text-sm text-ink-600">
                No sponsors exist yet. Create one under <a href="/sponsors" className="text-accent underline-offset-2 hover:underline">Sponsors</a>.
              </p>
            ) : null}
          </div>
        </Panel>
      ) : null}
    </div>
  );
}
